<template>
	<Modal
		width="600px"
		@keydown.esc="handleCancel"
	>
		<template #modal-title>
			{{ isEdit ? 'Update Integration' : 'Save Your Integration' }}
			<v-spacer />
			<IconButton
				:disabled="saving"
				@click="handleCancel"
			>
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<p class="subtitle-2">
				{{ isEdit
					? 'Update the name and description for your integration.'
					: 'Almost done! Give your integration a name and description so you can easily find it later.'
				}}
			</p>

			<validation-observer
				ref="form"
				tag="div"
				class="save-modal-form"
			>
				<TextField
					:value="localName"
					label="Name *"
					:rules="{ required: true, max: 50}"
					explainer="Enter a suitable name for this integration"
					autofocus
					@input="localName = $event"
				/>

				<Textarea
					:value="localNotes"
					label="Description (optional)"
					explainer="Enter any notes to help describe this integration (internal use only)"
					@input="localNotes = $event"
				/>
			</validation-observer>

			<div class="save-modal-info">
				<v-icon
					small
					class="mr-2"
					color="info"
				>
					mdi-information
				</v-icon>
				<span class="info-text">
					{{ isEdit
						? 'Your changes will be saved immediately.'
						: 'Your integration will be created and can be enabled from the overview page.'
					}}
				</span>
			</div>
		</template>

		<template #modal-footer>
			<SecondaryActionButton
				:disabled="saving"
				@click="handleCancel"
			>
				Cancel
			</SecondaryActionButton>
			<v-spacer />
			<PrimaryActionButton
				:loading="saving"
				:disabled="!localName.trim() || saving"
				@click="handleSave"
			>
				{{ isEdit ? 'Update' : 'Save' }}
			</PrimaryActionButton>
		</template>
	</Modal>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { VIcon, VSpacer } from 'vuetify/lib/components'
import Modal from '../../../../../../shared/components/modal.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Textarea from '../../../../../../shared/components/textarea.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'

export default {
	name: 'save-integration-modal',
	components: {
		ValidationObserver,
		Modal,
		VIcon,
		VSpacer,
		TextField,
		Textarea,
		PrimaryActionButton,
		SecondaryActionButton,
		IconButton
	},
	props: {
		value: {
			type: Boolean,
			default: false
		},
		currentName: {
			type: String,
			default: ''
		},
		currentNotes: {
			type: String,
			default: ''
		},
		isEdit: {
			type: Boolean,
			default: false
		},
		saving: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			localName: '',
			localNotes: ''
		}
	},
	watch: {
		currentName: {
			handler (newValue) {
				this.localName = newValue || ''
			},
			immediate: true
		},
		currentNotes: {
			handler (newValue) {
				this.localNotes = newValue || ''
			},
			immediate: true
		}
	},
	methods: {
		async handleSave () {
			// Validate the form
			const isValid = await this.$refs.form.validate()
			if (!isValid) {
				return
			}

			// Emit the save event with the values
			this.$emit('save', {
				name: this.localName.trim(),
				notes: this.localNotes.trim()
			})
		},
		handleCancel () {
			this.$emit('cancel')
		}
	}
}
</script>

<style lang="scss" scoped>
.save-modal-description {
	font-size: 16px;
	color: var(--global-muted-foreground);
	margin: 0 0 24px 0;
	line-height: 1.5;
}

.save-modal-form {
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 20px;
}

.save-modal-info {
	display: flex;
	align-items: flex-start;
	padding: 12px 16px;
	background: #e3f2fd;
	border-radius: var(--radius-sm, 4px);
	border-left: 4px solid var(--global-color-blue-darken-2);
}

.info-text {
	font-size: 14px;
	color: #1565c0;
	line-height: 1.4;
}
</style>
